import { create } from 'zustand';
import type { Scrapbook, Page } from '@shared/types';

interface EditorState {
  scrapbook: Scrapbook | null;
  pages: Page[];
  currentPageId: string | null;
  currentPageIndex: number;

  // Save state
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;

  // Selection state
  selectedElementIds: string[];

  // Actions
  setScrapbook: (scrapbook: Scrapbook | null) => void;
  setPages: (pages: Page[]) => void;
  setCurrentPage: (pageId: string) => void;
  setCurrentPageIndex: (index: number) => void;

  // Element selection
  selectElement: (id: string) => void;
  selectElements: (ids: string[]) => void;
  clearSelection: () => void;

  // Save actions
  markUnsaved: () => void;
  markSaved: () => void;
  setSaving: (saving: boolean) => void;
}

export const useEditorStore = create<EditorState>()((set, get) => ({
  scrapbook: null,
  pages: [],
  currentPageId: null,
  currentPageIndex: 0,

  isSaving: false,
  lastSaved: null,
  hasUnsavedChanges: false,

  selectedElementIds: [],

  setScrapbook: (scrapbook) => set({ scrapbook }),
  setPages: (pages) => set({ pages }),
  setCurrentPage: (pageId) => {
    const index = get().pages.findIndex((p) => p.id === pageId);
    set({ currentPageId: pageId, currentPageIndex: index >= 0 ? index : 0 });
  },
  setCurrentPageIndex: (index) => {
    const pages = get().pages;
    if (index >= 0 && index < pages.length) {
      set({ currentPageIndex: index, currentPageId: pages[index].id });
    }
  },

  selectElement: (id) => set({ selectedElementIds: [id] }),
  selectElements: (ids) => set({ selectedElementIds: ids }),
  clearSelection: () => set({ selectedElementIds: [] }),

  markUnsaved: () => set({ hasUnsavedChanges: true }),
  markSaved: () => set({ hasUnsavedChanges: false, lastSaved: new Date() }),
  setSaving: (isSaving) => set({ isSaving }),
}));
